import { Suggestion, Suggestions } from "@/components/ai-elements/suggestion";
import { stripUnrecognizedShaarpTags } from "@/lib/ai";
import { cn } from "@/lib/utils";
import { stripCitationMarkers } from "@shaarp/common";
import { ChevronRight } from "lucide-react";
import { useMemo } from "react";

type QuickReply = string;

/**
 * Extract quick-reply tags from text
 * Supports both formats for backward compatibility:
 * - New: [@shaarp/quick-reply="Reply text"]
 * - Legacy: [quick-reply="Reply text"]
 */
function extractQuickReplies(text: string): QuickReply[] {
  // Match both new and legacy formats
  const regex = /\[(?:@shaarp\/)?quick-reply="([^"]+)"\]/g;
  const replies: QuickReply[] = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    replies.push(match[1]);
  }

  return replies;
}

/**
 * Remove incomplete tags at the end of text (during streaming)
 */
function stripIncompleteTags(text: string): string {
  // Remove incomplete @shaarp/ tags at the end
  let result = text.replace(/\[@shaarp\/[^\]]*$/g, "");
  // Remove incomplete legacy tags at the end
  result = result.replace(/\[(?:quick-reply|suggestion)[^\]]*$/g, "");
  return result;
}

/**
 * Trailing run of markdown list markers with nothing left after them.
 *
 * The prompt asks for quick replies "at the very end of the answer", and models
 * regularly lay them out as a bullet list instead of the requested single line.
 * Once the tags are stripped, the bullets are all that is left: the answer ended
 * on two or three empty list items. Anchoring on the end of the text keeps a
 * lone dash inside a code block — and a `---` rule — untouched.
 */
const TRAILING_EMPTY_LIST_ITEMS_REGEX =
  /(?:\r?\n[ \t]*(?:[-*+]|\d+[.)])[ \t]*)+\s*$/;

/**
 * Remove all quick-reply and @shaarp/ tags from text for display
 * Supports both new and legacy formats
 */
export function stripQuickReplyTags(text: string): string {
  // First remove incomplete tags at the end (for streaming)
  let result = stripIncompleteTags(text);
  // Remove both new and legacy quick-reply tags
  result = result.replace(/\[(?:@shaarp\/)?quick-reply="[^"]+"\]/g, "");
  // Also remove any other unrecognized @shaarp/ tags
  result = stripUnrecognizedShaarpTags(result);
  // Drop the bullets the stripped tags left behind
  result = result.replace(TRAILING_EMPTY_LIST_ITEMS_REGEX, "");
  return result.trim();
}

/**
 * Check if text ends with a question (before quick-reply tags). A citation
 * pill after the question mark still leaves a question: `stripQuickReplyTags`
 * keeps the markers it renders, so the citations are read out here.
 */
function endsWithQuestion(text: string): boolean {
  const cleanText = stripCitationMarkers(stripQuickReplyTags(text)).trim();
  return cleanText.endsWith("?");
}

export type ChatQuickRepliesProps = {
  /** The full message text containing potential quick-reply tags */
  messageText: string;
  /** Callback when a quick reply is clicked */
  onQuickReplyClick: (reply: string) => void;
  /** Whether the replies are disabled */
  disabled?: boolean;
  /** Additional className */
  className?: string;
};

export function ChatQuickReplies({
  messageText,
  onQuickReplyClick,
  disabled = false,
  className,
}: ChatQuickRepliesProps) {
  const quickReplies = useMemo(
    () => extractQuickReplies(messageText),
    [messageText]
  );

  const hasQuestion = useMemo(
    () => endsWithQuestion(messageText),
    [messageText]
  );

  // Don't show if no quick replies or message doesn't end with question
  if (quickReplies.length === 0 || !hasQuestion) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex justify-end duration-300 animate-in slide-in-from-bottom-2 fade-in",
        className
      )}
    >
      <Suggestions className="justify-end">
        {quickReplies.slice(0, 3).map((reply, index) => (
          <Suggestion
            key={index}
            suggestion={reply}
            onClick={() => onQuickReplyClick(reply)}
            disabled={disabled}
          >
            {reply}
            <ChevronRight className="size-3.5" />
          </Suggestion>
        ))}
      </Suggestions>
    </div>
  );
}
